import { useState } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api, fmtMoney, formatErr } from "@/lib/api";
import Modal, { inputCls, inputErrCls, FieldError, labelCls, btnPrimary, btnAccent, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { useFormErrors } from "@/lib/formErrors";
import { TableCard, EmptyRow, theadCls, thCls, thNumCls, tdCls, tdNumCls, rowCls } from "@/components/Table";
import Pagination from "@/components/Pagination";
import { Plus, Trash2, Receipt, Edit2 } from "lucide-react";
import { toast } from "sonner";

const CATS = ["Rent", "Salary", "Utilities", "Transport", "Misc"];
const PAGE = 20;
const EMPTY = { amount: 0, category: "Rent", notes: "", date: "", status: "paid" };

export default function Expenses() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // expense being edited, or null = adding
  const [form, setForm] = useState(EMPTY);
  const [page, setPage] = useState(0);

  const { data } = useQuery({
    queryKey: ["expenses", page],
    queryFn: () => api.get("/expenses", { params: { skip: page * PAGE, limit: PAGE } })
      .then((r) => ({ items: r.data, count: Number(r.headers["x-total-count"] ?? r.data.length), amount: Number(r.headers["x-total-amount"] ?? 0) })),
    placeholderData: keepPreviousData,
  });
  const rows = data?.items ?? [];
  const count = data?.count ?? 0;
  const total = data?.amount ?? 0;

  const fe = useFormErrors();
  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, amount: parseFloat(form.amount), date: form.date || null };
      return editing ? api.put(`/expenses/${editing.id}`, body) : api.post("/expenses", body);
    },
    onSuccess: () => { toast.success(editing ? "Expense updated" : "Expense added"); qc.invalidateQueries({ queryKey: ["expenses"] }); setOpen(false); setEditing(null); setForm(EMPTY); fe.clear(); },
    onError: (e) => fe.handleError(e, toast.error),
  });
  const openNew = () => { setEditing(null); setForm(EMPTY); fe.clear(); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    const iso = e.date || e.created_at;
    // Show the local calendar day (matches the table), not the raw UTC string —
    // slicing the UTC timestamp would land a day early for shop-local dates.
    setForm({ amount: e.amount, category: e.category || "Rent", notes: e.notes || "", status: e.status || "paid", date: iso ? new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" }) : "" });
    fe.clear(); setOpen(true);
  };
  const del = useMutation({
    mutationFn: (id) => api.delete(`/expenses/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["expenses"] }); },
  });
  const toggleStatus = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/expenses/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["expenses"] }); },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="label-cap">Operations</div><h1 className="font-heading text-3xl mt-1">Expenses</h1></div>
        <button data-testid="add-expense-btn" onClick={openNew} className={btnAccent + " flex items-center gap-2"}><Plus size={16} /> Add Expense</button>
      </div>

      <div className="card-soft p-6 bg-agri-forest text-white inline-block">
        <div className="label-cap text-white/60">Total tracked</div>
        <div className="font-heading text-3xl mt-2 text-agri-amber tabular-nums">{fmtMoney(total)}</div>
      </div>

      <div className="text-xs text-agri-ink-2">{count} expense{count === 1 ? "" : "s"}</div>
      <TableCard>
        <thead className={theadCls}>
          <tr>
            <th className={thCls}>Date</th>
            <th className={thCls}>Category</th>
            <th className={thCls}>Notes</th>
            <th className={thCls}>Status</th>
            <th className={thNumCls}>Amount</th>
            <th className={thCls}></th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <EmptyRow colSpan={6} icon={Receipt}>No expenses yet.</EmptyRow>}
          {rows.map((e) => {
            const unpaid = (e.status || "paid") === "unpaid";
            return (
            <tr key={e.id} className={rowCls}>
              <td data-label="Date" className={`${tdCls} text-agri-ink-2 whitespace-nowrap`}>{new Date(e.date || e.created_at).toLocaleDateString()}</td>
              <td data-label="Category" className={tdCls}><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-agri-amber-dark">{e.category}</span></td>
              <td data-label="Notes" className={tdCls}>{e.notes}</td>
              <td data-label="Status" className={tdCls}>
                <button onClick={() => toggleStatus.mutate({ id: e.id, status: unpaid ? "paid" : "unpaid" })}
                  title="Click to toggle paid / unpaid"
                  className={`text-xs font-medium px-2 py-0.5 rounded-full transition-colors ${unpaid ? "bg-red-50 text-agri-danger hover:bg-red-100" : "bg-green-50 text-agri-forest-light hover:bg-green-100"}`}>
                  {unpaid ? "Unpaid" : "Paid"}
                </button>
              </td>
              <td data-label="Amount" className={`${tdNumCls} font-semibold`}>{fmtMoney(e.amount)}</td>
              <td className={`${tdCls} text-right rt-actions whitespace-nowrap`}>
                <button title="Edit" onClick={() => openEdit(e)} className="p-1.5 rounded hover:bg-black/5 text-agri-ink-2 hover:text-agri-forest align-middle"><Edit2 size={15} /></button>
                <button title="Delete" onClick={() => window.confirm("Delete this expense?") && del.mutate(e.id)} className="p-1.5 rounded hover:bg-red-50 text-agri-ink-2 hover:text-agri-danger ml-1 align-middle"><Trash2 size={15} /></button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </TableCard>
      <Pagination page={page} pageSize={PAGE} total={count} onPage={setPage} />

      <Modal open={open} onClose={() => { setOpen(false); setEditing(null); }} title={editing ? "Edit Expense" : "Add Expense"}>
        <form onSubmit={(ev) => { ev.preventDefault(); save.mutate(); }} className="space-y-3">
          <div>
            <label className={labelCls}>Amount</label>
            <NumberInput data-testid="expense-amount-input" required value={form.amount}
              onChange={(n) => { setForm({ ...form, amount: n }); fe.clearField("amount"); }}
              className={fe.fieldError("amount") ? inputErrCls : ""} />
            <FieldError>{fe.fieldError("amount")}</FieldError>
          </div>
          <div><label className={labelCls}>Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>{CATS.map((c) => <option key={c}>{c}</option>)}</select></div>
          <div><label className={labelCls}>Date</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></div>
          <div><label className={labelCls}>Status</label>
            <div className="grid grid-cols-2 gap-2">
              {["paid", "unpaid"].map((st) => (
                <button key={st} type="button" onClick={() => setForm({ ...form, status: st })}
                  className={`text-xs font-medium px-3 py-2 rounded-lg border capitalize transition-colors ${form.status === st ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"}`}>{st}</button>
              ))}
            </div>
          </div>
          <div><label className={labelCls}>Notes</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} /></div>
          <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={() => { setOpen(false); setEditing(null); }} className={btnOutline}>Cancel</button><button data-testid="expense-save-btn" className={btnPrimary} disabled={save.isPending}>{editing ? "Save changes" : "Add"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
